/* eslint no-use-before-define: 0 */
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
  Box,
  TextField,
} from "@mui/material";
import { Scaffold } from "../Scaffold";
import { setResult, setSelect, setAdvanceResult } from "../redux/actions";
import { useAuth } from "../AuthContext";
import LoadingSpinner from "../LoadingSpinner";
import { useForm } from "../hooks/useForm";
import { useAutofillShrink } from "../hooks/useAutofillShrink";
import { useNavigate } from "react-router-dom";
import { advancedSearch } from "../api/api";
import { stripWhitespace } from "../utils/cleaning";

export const AdvancedSearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [emailValue, setEmailValue] = useState<string>("");
  const [phoneValue, setPhoneValue] = useState<string>("");
  const [error, setError] = useState("");
  const email = useAutofillShrink(emailValue, setEmailValue);
  const phone = useAutofillShrink(phoneValue, (autofilled) =>
    setPhoneValue(stripWhitespace(autofilled)),
  );

  const onEmailChange = (e: any) => setEmailValue(e.target.value);
  const onPhoneChange = (e: any) => setPhoneValue(stripWhitespace(e.target.value));

  const [isLoading, setIsLoading] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    dispatch(setResult(null));
    dispatch(setSelect(null));
  }, [dispatch]);

  const initial = {
    email: "",
    phone: "",
  };

  const validate = () => "";

  const submit = async () => {
    const body = {
      email: emailValue,
      mobilePhoneNumber: phoneValue,
    };
    if (!emailValue && !phoneValue) {
      setError("Please enter an email or phone number");
      return;
    }

    setIsLoading(true);
    setError("");

    // Clear previous results so stale data never shows
    dispatch(setResult(null));
    dispatch(setAdvanceResult(null));
    dispatch(setSelect(null));

    try {
      const response = await advancedSearch(body, token ?? undefined).catch((error_: Error) => {
        if (error_.message === "Request failed with status code 404") {
          setError("Account Not Found");
        } else if (error_.message === "Request failed with status code 500") {
          setError("Something went wrong");
        } else if (error_.message === "timeout of 1000ms exceeded") {
          setError("Timed Out");
        } else {
          throw error_;
        }
        return undefined;
      });

      const dataObject = response as Record<string, unknown> | undefined;
      if (dataObject) {
        dispatch(setResult(dataObject));
        navigate("/advanceresult");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const { onSubmit } = useForm({
    initial,
    validate,
    submit,
  });

  return (
    <Scaffold
      center
      navBar
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Card
          sx={{ width: 425, boxShadow: "0 0 5px 2px #C5C6D0", padding: "30px" }}
        >
          <CardContent>
            <Stack
              spacing={2}
              direction="row"
            >
              <Box></Box>
              <Stack spacing={2}>
                <Stack
                  spacing={2}
                  width={300}
                >
                  <Typography variant="h6">Advanced Search</Typography>
                  <TextField
                    size="small"
                    label="Email"
                    onChange={onEmailChange}
                    value={emailValue}
                    sx={{
                      "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active": {
                        WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                        WebkitTextFillColor: "inherit",
                        transition: "background-color 5000s ease-in-out 0s",
                      },
                    }}
                    slotProps={{
                      htmlInput: {
                        ...email.autofillProps,
                        autoComplete: "off",
                        name: "medley-lookup-email",
                        "aria-autocomplete": "none",
                      },
                      inputLabel: {
                        shrink: email.shouldShrink,
                      },
                    }}
                  />
                  <TextField
                    size="small"
                    label="Phone"
                    onChange={onPhoneChange}
                    value={phoneValue}
                    sx={{
                      "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active": {
                        WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                        WebkitTextFillColor: "inherit",
                        transition: "background-color 5000s ease-in-out 0s",
                      },
                    }}
                    slotProps={{
                      htmlInput: {
                        ...phone.autofillProps,
                        autoComplete: "off",
                        name: "medley-lookup-phone",
                        "aria-autocomplete": "none",
                      },
                      inputLabel: {
                        shrink: phone.shouldShrink,
                      },
                    }}
                  />
                </Stack>
                <Button
                  variant="contained"
                  onClick={onSubmit}
                  color="primary"
                  sx={{ paddingTop: "10px" }}
                  disabled={
                    Boolean(emailValue === "" && phoneValue === "")
                  }
                >
                  Search
                </Button>
                {error && (
                  <Typography
                    variant="body1"
                    color="red"
                    fontWeight="500"
                  >
                    {error}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Scaffold>
  );
};
